import React, { useMemo } from 'react';
import styled, { css, keyframes } from 'styled-components';

// Butterflies behind the public program, after the ones of the HEARTS cover video on the homepage:
// real-looking butterflies (pointed forewings and angular hindwings with veins, a dark rim with pale
// spots, an eyespot). The motion follows what was measured frame by frame in that video:
// - they flutter around the flowers, turning sharply all the time: a small group at every flower,
//   lung and heart of the band (`flowers`, ProgramSectionView's SCATTER) and at the lung flower in the
//   corner; now and then one flies on from its flower to the nearest one on the same side or across
//   the band to one on the other side, flutters there a while and comes back, at about 150 px a second;
// - every flyer twitches about ten times a second (half a body length, the body rocking) and beats
//   its wings fast and without pause (about five strokes a second);
// - the flyers are bright: orange, pink and red, pale blue with a dark rim, yellow-green.
// (The video's grey ones sitting on the heart were left out on purpose.)
// In 3D (CSS perspective): body and wings are separate parts, the wings hinge on the body axis, each
// butterfly is seen from its own angle (from above, obliquely, almost from the side); the body turns
// to where it flies and is mirrored when it flies to the left.
//
// The paths come from a fixed seed, so the swarm looks the same on every visit and in every test.
// Decoration only: in front of the flowers (z-index 0), behind the program (SectionInner, 2), never
// clickable, hidden from assistive technology, never printed. Transform-only animations; with
// "reduce motion" they all sit still and the travellers stay away. Below 1024 px, where only the
// flowers marked for phones show, only those flowers' groups show, fluttering in a smaller space, and
// none travels.

// ------------------------------------------------------------------ motion

// A small deterministic random generator (mulberry32).
const random = (seed) => {
  let a = seed >>> 0;
  return () => {
    a = (a + 0x6d2b79f5) >>> 0;
    let t = a;
    t = Math.imul(t ^ (t >>> 15), t | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
};

// A point of a path, in the area the butterfly keeps to: x is `xp` % of the area's width plus `x` px,
// y is `yp` % of its height plus `y` px (an area spanning two flowers is as high as the band between
// them, which is only known in %). `t`: the share of the cycle to the next point; `ease`: how it
// gets there (fluttering slows at every turn, a flight is even).
const point = (x, y, { xp = 0, yp = 0, t = 1, ease = 'ease-in-out' } = {}) => ({ x, y, xp, yp, t, ease });

const length = (pct, px) => (pct ? `calc(${+pct.toFixed(2)}% + ${px.toFixed(1)}px)` : `${px.toFixed(1)}px`);

// The keyframes of a closed path: `move` for where the butterfly is (on a box as large as the area,
// so that % means the area's size), `turn` for where it heads (on the butterfly itself): up to 50
// degrees into a climb or drop, mirrored (scaleX -1) when it flies to the left. `size`: about how
// large the area is in px, only for the heading.
const pathKeyframes = (pts, size = { w: 0, h: 0 }) => {
  const total = pts.reduce((sum, p) => sum + p.t, 0);
  const px = (p) => [(p.xp / 100) * size.w + p.x, (p.yp / 100) * size.h + p.y];
  const move = [];
  const turn = [];
  let at = 0;
  pts.forEach((p, i) => {
    const [x, y] = px(p);
    const [nx, ny] = px(pts[(i + 1) % pts.length]);
    const dx = nx - x;
    const dy = ny - y;
    const angle = Math.max(-50, Math.min(50, (Math.atan2(dy, Math.abs(dx) || 0.001) * 180) / Math.PI));
    const stop = `${at.toFixed(2)}%`;
    const ease = `animation-timing-function: ${p.ease};`;
    move.push(`${stop} { transform: translate(${length(p.xp, p.x)}, ${length(p.yp, p.y)}); ${ease} }`);
    turn.push(`${stop} { transform: scaleX(${dx < 0 ? -1 : 1}) rotate(${angle.toFixed(1)}deg); ${ease} }`);
    at += (p.t / total) * 100;
  });
  move.push(move[0].replace(/^[\d.]+%/, '100%'));
  turn.push(turn[0].replace(/^[\d.]+%/, '100%'));
  return { move: keyframes`${move.join('\n')}`, turn: keyframes`${turn.join('\n')}` };
};

// Fluttering about a spot: `n` waypoints inside a w x h box (in px, from its top left), sudden turns
// every step.
const hoverPoints = (rand, { w, h, n }) => {
  const pts = [];
  let x = rand() * w;
  let y = rand() * h;
  let dir = rand() * Math.PI * 2;
  for (let i = 0; i < n; i += 1) {
    pts.push([x, y]);
    dir += (rand() - 0.5) * 3.4;
    const step = (0.18 + rand() * 0.32) * Math.min(w, h);
    x = Math.max(0, Math.min(w, x + Math.cos(dir) * step));
    y = Math.max(0, Math.min(h, y + Math.sin(dir) * step));
    if (x === 0 || x === w || y === 0 || y === h) dir += Math.PI * (0.6 + rand() * 0.8);
  }
  return pts;
};

// The largest butterfly is about this wide; paths keep it inside their box.
const BUTTERFLY = 30;

// A group's path: fluttering about its flower and back to where it began, with uneven timing.
const hoverPath = (rand, box) => pathKeyframes(
  hoverPoints(rand, { w: box.w - BUTTERFLY, h: box.h, n: 22 }).map(([x, y]) => point(x, y, { t: 0.6 + rand() })),
);

// The twitch on top of the path: about ten jerks a second, offsets in % of the butterfly's own size.
const twitch = (rand, amp) => {
  const n = 8;
  const pts = Array.from({ length: n }, () => [
    (rand() - 0.5) * 2 * amp,
    (rand() - 0.5) * 2.4 * amp,
    (rand() - 0.5) * 2 * amp * 1.2,
  ]);
  const frames = pts.map((p, i) => `${((i / n) * 100).toFixed(1)}% { transform: translate(${p[0].toFixed(0)}%, ${p[1].toFixed(0)}%) rotate(${p[2].toFixed(0)}deg); }`);
  frames.push(frames[0].replace(/^[\d.]+%/, '100%'));
  return keyframes`${frames.join('\n')}`;
};

const TWITCHES = (() => {
  const rand = random(7);
  return {
    hover: [0, 1, 2, 3].map(() => twitch(rand, 22)),
    travel: [0, 1, 2, 3].map(() => twitch(rand, 12)),
  };
})();

// Wing angle in flight: fast strokes without pause, up over the back to `up`, down to `down`. The
// left wing turns positive, the right one mirrored (sign -1).
const flapBeat = (sign) => keyframes`
  0%, 100% { transform: rotateY(${sign * -14}deg); }
  50% { transform: rotateY(${sign * 76}deg); }
`;

const WINGS = { left: flapBeat(1), right: flapBeat(-1) };

// The abdomen bobs a little with every stroke.
const bob = keyframes`
  0%, 100% { transform: rotateX(0deg) translateZ(0.5px); }
  50% { transform: rotateX(14deg) translateZ(0.5px); }
`;

const still = css`
  @media (prefers-reduced-motion: reduce) {
    animation: none !important;
  }
`;

// ------------------------------------------------------------------ layout

const Sky = styled.div`
  position: absolute;
  inset: 0;
  z-index: 1;
  overflow: hidden;
  pointer-events: none;
  container-type: inline-size;

  @media print {
    display: none;
  }
`;

// The area a group keeps to: the box around its flower, at the band's edge; a traveller's area is
// the band's whole width, from the top of the upper of its two flowers to the bottom of the lower one.
// Below 1024 px only the areas of the flowers that still show there.
const Area = styled.div`
  position: absolute;
  top: ${(p) => p.$top};
  ${(p) => (p.$side === 'wide' ? css`left: 0; right: 0;` : css`${p.$side}: 0; width: ${p.$w}px;`)}
  height: ${(p) => p.$h};

  @media screen and (max-width: 1024px) {
    ${(p) => !p.$small && css`display: none;`}
  }
`;

// Moves the butterfly along its path: as large as its area, the butterfly at its top left. Below
// 1024 px the butterflies with a smaller path take it, the others do not show.
const Flyer = styled.div`
  position: absolute;
  inset: 0;
  animation: ${(p) => p.$path.move} ${(p) => p.$duration}s linear ${(p) => p.$delay}s infinite;
  ${still}

  @media screen and (max-width: 1024px) {
    ${(p) => (p.$small ? css`animation-name: ${p.$small.move};` : css`display: none;`)}
  }

  @media (prefers-reduced-motion: reduce) {
    ${(p) => p.$travels && css`display: none;`}
  }
`;

// Turns the butterfly to where it flies, in step with its Flyer.
const Heading = styled.div`
  position: absolute;
  left: 0;
  top: 0;
  animation: ${(p) => p.$path.turn} ${(p) => p.$duration}s linear ${(p) => p.$delay}s infinite;
  ${still}

  @media screen and (max-width: 1024px) {
    ${(p) => p.$small && css`animation-name: ${p.$small.turn};`}
  }
`;

const Twitch = styled.div`
  animation: ${(p) => p.$anim} ${(p) => p.$period}s linear ${(p) => p.$delay}s infinite;
  ${still}
`;

// One butterfly: a 3D rig seen from its own angle (tilt), heading along the flight.
const Body3D = styled.div`
  width: ${(p) => p.$size}px;
  height: ${(p) => Math.round(p.$size * 0.8)}px;
  perspective: ${(p) => p.$size * 9}px;
  opacity: ${(p) => p.$opacity};
  filter: drop-shadow(0 3px 2px rgba(40, 30, 40, 0.16));

  @media screen and (max-width: 768px) {
    width: ${(p) => Math.round(p.$size * 0.8)}px;
    height: ${(p) => Math.round(p.$size * 0.64)}px;
  }
`;

const Rig = styled.div`
  position: relative;
  width: 100%;
  height: 100%;
  transform-style: preserve-3d;
  transform: rotateX(${(p) => p.$tilt}deg) rotateZ(${(p) => p.$heading}deg);
`;

const WingBox = styled.div`
  position: absolute;
  top: 0;
  width: 50%;
  height: 100%;
  transform-style: preserve-3d;
  ${(p) => (p.$side === 'left'
    ? css`left: 0; transform-origin: 100% 50%;`
    : css`left: 50%; transform-origin: 0% 50%;`)}
  animation: ${(p) => p.$anim} ${(p) => p.$period}s ease-in-out ${(p) => p.$delay}s infinite;
  ${still}

  svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`;

const Torso = styled.div`
  position: absolute;
  left: 50%;
  top: 0;
  width: 12%;
  height: 100%;
  margin-left: -6%;
  transform-origin: 50% 35%;
  animation: ${bob} ${(p) => p.$period}s ease-in-out ${(p) => p.$delay}s infinite;
  ${still}

  svg {
    display: block;
    width: 100%;
    height: 100%;
    overflow: visible;
  }
`;

// ------------------------------------------------------------------ drawing

// Wing colours: [inner, outer, rim].
const PALETTES = {
  orange: ['#ffdcaa', '#ef8a2c', '#4a2610'],
  pink: ['#ffc8da', '#dc5088', '#4d1830'],
  red: ['#f7a594', '#cf3b2c', '#3a1a15'],
  blue: ['#f2f9fd', '#9fcde3', '#1e3446'],
  lime: ['#f3f7b4', '#bcd245', '#3a4214'],
};

// The left wing pair in a 32 x 50 box, hinge on the right edge (x = 32), head at the top.
const LeftWing = ({ id, colors }) => {
  const [inner, outer, rim] = colors;
  return (
    <svg viewBox="0 0 32 50" aria-hidden="true" focusable="false">
      <defs>
        <linearGradient id={id} x1="1" y1="0.5" x2="0" y2="0.5">
          <stop offset="0" stopColor={inner} />
          <stop offset="1" stopColor={outer} />
        </linearGradient>
      </defs>
      <path d="M31 21 C24 14 13 6 1 1 L4 8 C5 13 7 17 10 21 C16 24 24 25 31 25 Z" fill={`url(#${id})`} stroke={rim} strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M31 26 C22 26 15 28 11 32 L12 39 L17 49 L20 45 C25 41 29 36 31 30 Z" fill={`url(#${id})`} stroke={rim} strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M1 1 L4 8 C5 12 6 15 8 18 L10 16 C8 12 7.5 8 8 4.5 Z" fill={rim} opacity="0.85" />
      <circle cx="4.6" cy="7" r="0.8" fill={inner} />
      <circle cx="5.8" cy="11" r="0.75" fill={inner} />
      <circle cx="7.4" cy="14.5" r="0.65" fill={inner} />
      <path d="M31 22 C22 15 13 8 4 4 M31 23 C22 19 14 15 6 12 M30 24 C23 23 17 22 11 20 M31 28 C24 31 18 35 14 40 M31 29 C26 34 22 40 18 47"
        fill="none" stroke={rim} strokeWidth="0.6" opacity="0.45" />
      <circle cx="18" cy="38" r="2.2" fill={rim} opacity="0.75" />
      <circle cx="18" cy="38" r="1" fill={outer} />
      <circle cx="18.4" cy="37.6" r="0.4" fill="#fff" opacity="0.8" />
    </svg>
  );
};

// Thorax, abdomen, head and clubbed antennae, in a 6 x 50 box.
const TorsoSvg = () => (
  <svg viewBox="0 0 6 50" aria-hidden="true" focusable="false">
    <ellipse cx="3" cy="27" rx="1.8" ry="9.5" fill="#3a3129" />
    <circle cx="3" cy="17" r="2" fill="#3a3129" />
    <path d="M3 15.5 C4 10 6 7 9 5 M3 15.5 C2 10 0 7 -3 5" fill="none" stroke="#3a3129" strokeWidth="0.7" strokeLinecap="round" />
    <circle cx="9" cy="5" r="0.8" fill="#3a3129" />
    <circle cx="-3" cy="5" r="0.8" fill="#3a3129" />
  </svg>
);

// One butterfly. tilt: 0 seen from above, 50-65 obliquely, 75+ almost from the side. It heads to the
// right; its path turns and mirrors it.
const Butterfly = ({ id, palette, size, tilt, period, delay }) => {
  const colors = PALETTES[palette] || PALETTES.orange;
  return (
    <Body3D $size={size} $opacity={0.95}>
      <Rig $tilt={tilt} $heading={90}>
        <WingBox $side="left" $anim={WINGS.left} $period={period} $delay={delay}>
          <LeftWing id={`${id}-l`} colors={colors} />
        </WingBox>
        <WingBox $side="right" $anim={WINGS.right} $period={period} $delay={delay}>
          <div style={{ width: '100%', height: '100%', transform: 'scaleX(-1)' }}>
            <LeftWing id={`${id}-r`} colors={colors} />
          </div>
        </WingBox>
        <Torso $period={period} $delay={delay}><TorsoSvg /></Torso>
      </Rig>
    </Body3D>
  );
};

// ------------------------------------------------------------------ the swarm

// About how large the band is on a wide screen, only for the heading of the travellers.
const BAND = { w: 1400, h: 7000 };

// Where a flower is, from its place in ProgramSectionView's SCATTER: its top (`at` % of the band plus
// `px`), its side, and the box the butterflies flutter in, from the band's edge over the part of the
// flower in sight and a little beyond it; `small`: the box below 1024 px for the flowers that show
// there (Scatter: 45 % of the size, 40 px past the edge).
const spotOf = (f) => ({
  side: f.side,
  at: parseFloat(f.top),
  px: 0,
  box: { w: f.width + f.offset + 50, h: Math.round(f.width * 0.9) },
  small: f.phone ? { w: Math.round(f.width * 0.45), h: Math.round(f.width * 0.45 * 0.9) } : null,
});

// The lung flower in the top right corner (programStyles' Deco "lung": 360 px, 70 px past the edge,
// 30 px from the top; on phones 170 px, 60 px past it), with a larger group.
const LUNG = { side: 'right', at: 0, px: 30, box: { w: 340, h: 300 }, small: { w: 150, h: 150 }, n: 4 };

// A butterfly at (x, y) in the box of `spot`, as a point in a traveller's area `height` px plus the
// band between its two flowers high: at its top for the upper flower, at its bottom for the lower one.
const inBand = (spot, x, y, { upper, height, ...rest }) => point(
  spot.side === 'right' ? x - spot.box.w : x,
  upper ? y : y - height,
  { ...rest, xp: spot.side === 'right' ? 100 : 0, yp: upper ? 0 : 100 },
);

const SPEED = 150; // px a second in flight

// A traveller's path: it flutters at flower a, flies to flower b in a loose wave, flutters there and
// flies back. Returns the keyframes, the cycle's length in seconds and the area (top and height).
const travelPath = (rand, a, b) => {
  const [up, low] = a.at <= b.at ? [a, b] : [b, a];
  const height = Math.max(up.box.h, low.box.h);
  const area = {
    top: `calc(${up.at}% + ${up.px}px)`,
    h: `calc(${low.at - up.at}% + ${low.px - up.px + height}px)`,
    size: { w: BAND.w, h: ((low.at - up.at) / 100) * BAND.h + height },
  };
  const flutter = (spot) => hoverPoints(rand, { w: spot.box.w - BUTTERFLY, h: spot.box.h - BUTTERFLY, n: 14 })
    .map(([x, y]) => inBand(spot, x, y, { upper: spot === up, height, t: 0.35 + rand() * 0.4 }));
  const px = (p) => [(p.xp / 100) * area.size.w + p.x, (p.yp / 100) * area.size.h + p.y];
  // From the last point at one flower to the first at the other, evenly.
  const flight = (from, to) => {
    const [fx, fy] = px(from);
    const [tx, ty] = px(to);
    const distance = Math.hypot(tx - fx, ty - fy);
    const steps = Math.max(2, Math.round(distance / 250));
    const t = distance / SPEED / steps;
    const out = [{ ...from, t, ease: 'linear' }];
    for (let i = 1; i < steps; i += 1) {
      const f = i / steps;
      const mix = (k) => from[k] + (to[k] - from[k]) * f;
      out.push(point(mix('x') + (rand() - 0.5) * 60, mix('y') + (rand() - 0.5) * 90,
        { xp: mix('xp'), yp: mix('yp'), t, ease: 'linear' }));
    }
    return out;
  };
  const atA = flutter(a);
  const atB = flutter(b);
  const pts = [
    ...atA.slice(0, -1), ...flight(atA[atA.length - 1], atB[0]),
    ...atB.slice(1, -1), ...flight(atB[atB.length - 1], atA[0]),
  ];
  return { path: pathKeyframes(pts, area.size), duration: +pts.reduce((sum, p) => sum + p.t, 0).toFixed(1), area };
};

// Flyer colours as counted in the video: mostly orange and pale blue, then pink, yellow-green, red.
const FLYING = ['orange', 'orange', 'orange', 'blue', 'blue', 'blue', 'pink', 'pink', 'lime', 'lime', 'red'];

// Everything about each butterfly, from one seed: colour, size, view angle, wing timing, its path,
// and the areas they keep to. Every flower gets a group of two or three (the ones that show on phones
// three, two of which still flutter there; the lung flower four); from about two flowers in three one
// flies on now and then, to the nearest flower on the same side or across the band.
const swarmOf = (flowers) => {
  const rand = random(20261105);
  const pick = (list) => list[Math.floor(rand() * list.length)];
  const spots = [LUNG, ...flowers.map(spotOf)];
  const areas = [];
  const swarm = [];
  const looks = (travels) => {
    const palette = pick(FLYING);
    return {
      delay: -+(rand() * 30).toFixed(2),
      palette,
      size: Math.round((palette === 'blue' ? 26 : 18) + rand() * 12),
      tilt: Math.round(10 + rand() * 66),
      period: +(0.17 + rand() * 0.09).toFixed(3),
      twitch: pick(TWITCHES[travels ? 'travel' : 'hover']),
      twitchPeriod: +(0.7 + rand() * 0.3).toFixed(2),
    };
  };

  spots.forEach((spot) => {
    const ai = areas.length;
    areas.push({
      kind: 'hover', top: `calc(${spot.at}% + ${spot.px}px)`, side: spot.side, w: spot.box.w, h: `${spot.box.h}px`, small: !!spot.small,
    });
    const n = spot.n || (spot.small ? 3 : 2 + (rand() < 0.5 ? 1 : 0));
    for (let i = 0; i < n; i += 1) {
      swarm.push({
        key: `${ai}-${i}`,
        area: ai,
        ...looks(false),
        path: hoverPath(rand, spot.box),
        small: spot.small && i < 2 ? hoverPath(rand, spot.small) : null,
        duration: +(8 + rand() * 5).toFixed(1),
      });
    }
  });

  const pairs = new Set();
  spots.forEach((a, i) => {
    if (rand() < 0.35) return;
    const across = rand() < 0.5;
    const [b] = spots
      .filter((s) => s !== a && (s.side !== a.side) === across)
      .sort((p, q) => Math.abs(p.at - a.at) - Math.abs(q.at - a.at));
    if (!b) return;
    const pair = [i, spots.indexOf(b)].sort((x, y) => x - y).join('-');
    if (pairs.has(pair)) return;
    pairs.add(pair);
    const { path, duration, area } = travelPath(rand, a, b);
    const ai = areas.length;
    areas.push({ kind: 'travel', top: area.top, side: 'wide', h: area.h, small: false });
    swarm.push({ key: `${ai}-0`, area: ai, travels: true, ...looks(true), path, small: null, duration });
  });

  return { areas, swarm };
};

// `flowers`: the flowers of the band (ProgramSectionView's SCATTER), fixed.
const ProgramButterflies = ({ flowers = [] }) => {
  const { areas, swarm } = useMemo(() => swarmOf(flowers), [flowers]);
  return (
    <Sky aria-hidden="true" data-testid="program-butterflies">
      {areas.map((area, ai) => (
        // eslint-disable-next-line react/no-array-index-key -- a fixed list
        <Area key={ai} data-kind={area.kind} $top={area.top} $side={area.side} $w={area.w} $h={area.h} $small={area.small}>
          {swarm.filter((b) => b.area === ai).map((b) => (
            <Flyer key={b.key} $path={b.path} $small={b.small} $duration={b.duration} $delay={b.delay} $travels={b.travels}>
              <Heading $path={b.path} $small={b.small} $duration={b.duration} $delay={b.delay}>
                <Twitch $anim={b.twitch} $period={b.twitchPeriod} $delay={b.delay}>
                  <Butterfly id={`bf-${b.key}`} palette={b.palette} size={b.size} tilt={b.tilt} period={b.period} delay={b.delay} />
                </Twitch>
              </Heading>
            </Flyer>
          ))}
        </Area>
      ))}
    </Sky>
  );
};

export default ProgramButterflies;
